import rgbHex from 'rgb-hex';
import tinycolor from 'tinycolor2';
import { v4 as uuid } from 'uuid';
import { EffectSolidGradient, Layer as ParseLayer, RGB, RGBA } from '@meitu-graphic/ag-psd';
import { computerBgRatio } from '@meitu/saas-editor-core-next/es/utils/layer-kit';
import { toFixedNumber, translateAngleToXY } from '@meitu/saas-editor-core-next/es/utils/math-kit';
import {
    ImageEffectStrokeType,
    ImgModeType,
    LayerType,
    MaskType,
    type IImageEffectGlow,
    type IImageEffectShadow,
    type IImageEffectStroke,
    type IImageLayerProps,
    type ILayerProps,
    type ITextLayerProps,
} from '@meitu/saas-editor-core-next/es/version-types';
import { LayerNameSource, PsdParseWarnType, UploadImage, WarnOrErrorCallbackFunc } from './types';

const colorMax255 = (color: number) => {
    if (color > 255) {
        color = 255;
    }

    return color;
};

const getOpacityOrDefault = (value = 1) => {
    return value;
};

export const parseImageShadow = (layer: ParseLayer) => {
    const textShadow = [] as IImageEffectShadow[];

    if (layer.effects && layer.effects.dropShadow) {
        layer.effects.dropShadow.forEach((data) => {
            if (!data.enabled) {
                return;
            }

            const rgb = (data.color as RGB) || { r: 255, g: 255, b: 255 };
            const color = `#${rgbHex(
                colorMax255(rgb.r),
                colorMax255(rgb.g),
                colorMax255(rgb.b),
                getOpacityOrDefault(data.opacity),
            )}`;

            const angle = data.angle || 0;
            const distance = data.distance?.value || 0;
            const size = data.size?.value || 0;
            textShadow.push({
                id: uuid(),
                color,
                x: translateAngleToXY(angle, distance).x / 1000 / 0.66,
                y: translateAngleToXY(angle, distance).y / 1000 / 0.66,
                // 半径
                blur: size / 100,
                hidden: false,
                // 是否可编辑
                editable: true,
            });
        });
    }

    return textShadow;
};

/** 解析发光 */
export const parseImageGlow = (layer: ParseLayer) => {
    const textGlow = [] as IImageEffectGlow[];

    if (layer.effects && layer.effects.outerGlow && layer.effects.outerGlow.enabled) {
        const data = layer.effects.outerGlow;

        const rgb = (data.color as RGB) || { r: 255, g: 255, b: 255 };
        const color = `#${rgbHex(
            colorMax255(rgb.r),
            colorMax255(rgb.g),
            colorMax255(rgb.b),
            getOpacityOrDefault(data.opacity),
        )}`;

        textGlow.push({
            id: uuid(),
            color,
            // FIXME  0.75为估算值 不准，
            width: (data.size?.value || 0) / 100 / 0.75,
            hidden: false,
            editable: true,
        });
    }
    return textGlow;
};

/** 解析描边 */
export const parseImageStroke = (layer: ParseLayer) => {
    const textStroke = [] as IImageEffectStroke[];

    if (layer.effects && layer.effects.stroke) {
        layer.effects.stroke.forEach((data) => {
            if (!data.enabled) {
                return;
            }

            const rgb = (data.color as RGB) || { r: 255, g: 255, b: 255 };
            const color = `#${rgbHex(
                colorMax255(rgb.r),
                colorMax255(rgb.g),
                colorMax255(rgb.b),
                getOpacityOrDefault(data.opacity),
            )}`;
            textStroke.push({
                id: uuid(),
                color,
                width: ((data.size?.value || 0) / 100) * 1.5,
                hidden: false,
                editable: true,
                strokeType: ImageEffectStrokeType.normal,
            });
        });
    }
    return textStroke;
};

/**
 * 抽取 Canvas 图像转换为文件
 * @param {string} fileName
 * @param {HTMLCanvasElement} canvas
 */
export function transformCanvasToFile(fileName: string, canvas: HTMLCanvasElement, imageType = 'image/png') {
    const dataURL = canvas.toDataURL(imageType, 0.95);
    const [originalMime, base64Data] = dataURL.split(',') as any;
    const mime = originalMime.match(/:(.*?);/)[1];
    const atobData = atob(base64Data);
    const fileBits = new Uint8Array(atobData.length);
    let current = atobData.length;

    while (current--) {
        fileBits[current] = atobData.charCodeAt(current);
    }

    return new File([fileBits], fileName, { type: mime });
}

/**
 *
 * @param psd psd json 源数据
 * @param uploadFn 上传图片回调函数
 */
export const getUrlByCanvas = async (
    name: string,
    canvas: HTMLCanvasElement,
    uploadFn: UploadImage,
    imageType = 'image/png',
) => {
    const url = await uploadFn(transformCanvasToFile(name, canvas, imageType));

    return url;
};

/**
 * @description 处理蒙版图片
 * @param ctx
 * @param width
 * @param height
 * @param defaultColor
 */
export const handleMaskImgData = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    defaultColor: number,
) => {
    // 图层蒙版分白底蒙版跟黑底蒙版
    // 添加白底蒙版（defaultColor = 255）后，不想要的可以直接擦除
    // 添加黑底蒙版(defaultColor = 0) 后 ，整张图片不显示，先要显示什么就擦出来
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const len = data.length / 4;
    for (let i = 0; i < len; i++) {
        const num = i * 4;
        if (defaultColor === 0) {
            if (data[num] === 0) {
                data[num + 3] = 0;
            } else {
                data[num + 3] = data[num];
            }
        } else if (defaultColor === 255) {
            if (data[num] !== defaultColor) {
                data[num + 3] = data[num];
            }
        }
    }
    ctx.putImageData(imageData, 0, 0);
};

/**
 * @description 补齐canvas 透明位置
 */
export const polishingCanvas = (ctx: CanvasRenderingContext2D, width: number, height: number, defaultColor: number) => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const len = data.length / 4;
    // 补全画布
    for (let i = 0; i < len; i++) {
        const num = i * 4;
        if (data[num + 3] === 0) {
            data[num] = defaultColor;
            data[num + 1] = defaultColor;
            data[num + 2] = defaultColor;
            data[num + 3] = 255;
        }
    }
    ctx.putImageData(imageData, 0, 0);
};

/**
 * @description 获取图层蒙版的蒙版canvas
 * @param v 图片图层
 * @returns canvas
 */
export const getImgMaskUrl = async (layer: ParseLayer, uploadFile: UploadImage) => {
    const { canvas, left, top, mask } = layer;

    if (!canvas || !mask) {
        return null;
    }

    const { width, height } = canvas;

    const { canvas: maskCanvas, left: maskLeft, top: maskTop, defaultColor } = mask;

    if (!maskCanvas || !maskLeft || !maskTop! || defaultColor === undefined || !left || !top) {
        return null;
    }

    const newCanvas = document.createElement('canvas');
    const ctx = newCanvas.getContext('2d') as CanvasRenderingContext2D;
    newCanvas.width = width;
    newCanvas.height = height;
    ctx.drawImage(maskCanvas, maskLeft - left, maskTop - top);
    if (ctx) {
        // 补全画布
        polishingCanvas(ctx, width, height, defaultColor);

        // 抠图
        handleMaskImgData(ctx, width, height, defaultColor);
    }

    const url = await getUrlByCanvas(layer.name || '', newCanvas, uploadFile);
    return url;
};

export const getWarningInfoObj = (layer: ILayerProps, info: string) => {
    return {
        name: layer.title,
        id: layer.id,
        info,
    };
};

export const getWarningInfos = (layers: ILayerProps[], replaceFonts: any[]) => {
    const warnInfos = [] as {
        name: string;
        info: string;
        id: string;
    }[];

    if (replaceFonts.length > 0) {
        replaceFonts.forEach((item) => {
            warnInfos.push(getWarningInfoObj(item.layer, `字体被被替换${item.replaceFont}`));
        });
    }
    layers.forEach((layer, index) => {
        const { width, height } = layer;

        if (index === 0 && layer.layerType !== LayerType.BACKGROUND_LAYER) {
            warnInfos.push(getWarningInfoObj(layer, '没有背景图层或者背景图层不在第一个'));
            return;
        }

        if (width === 0) {
            warnInfos.push(getWarningInfoObj(layer, '图层宽度为0'));
        } else if (width < 10) {
            warnInfos.push(getWarningInfoObj(layer, '图层宽度小于10是否有问题'));
        }

        if (height === 0) {
            warnInfos.push(getWarningInfoObj(layer, '图层高度为0'));
        } else if (height < 10) {
            warnInfos.push(getWarningInfoObj(layer, '图层高度小于10是否有问题'));
        }

        if (layer.layerType === LayerType.IMAGE_LAYER) {
            const data = layer.data as IImageLayerProps;
            const { url } = data;

            if (url === '') {
                warnInfos.push(getWarningInfoObj(layer, '图片url不存在'));
            }
        }

        if (layer.layerType === LayerType.TEXT_LAYER) {
            const data = layer.data as ITextLayerProps;
            const { lineSpacing, letterSpacing, text, fontSize } = data;

            if (letterSpacing < 0) {
                warnInfos.push(getWarningInfoObj(layer, '字间距小于0'));
            }

            if (lineSpacing < 1) {
                warnInfos.push(getWarningInfoObj(layer, '行间距小于字体大小'));
            }

            if (fontSize < 12) {
                warnInfos.push(getWarningInfoObj(layer, '字体大小小于12px'));
            }

            if (!text || !text.replace(/\s*/g, '')) {
                warnInfos.push(getWarningInfoObj(layer, '文本字段为空，可能是个空图层'));
            }
        }

        if (layer.layerType === LayerType.GROUP_LAYER && layer.children.length === 0) {
            warnInfos.push(getWarningInfoObj(layer, '图层组没有子图层，可能是个无效图层组'));
        }
    });

    return warnInfos;
};

export const setCutMaskLayers = async (
    stageWidth: number,
    stageHeight: number,
    clientLayers: ILayerProps[],
    layers: ParseLayer[],
    uploadFile: UploadImage,
    warnOrErrorCallback?: WarnOrErrorCallbackFunc,
    parseNoLimit = false,
) => {
    if (layers.length === 0) return { width: 0, height: 0 };
    const clientLayersMap = clientLayers.reduce(
        (total, layer) => {
            total[layer.id] = layer;
            return total;
        },
        {} as { [key: string]: ILayerProps },
    );

    const cutMaskLayers = layers.reduce((total, item, index) => {
        if (item.clipping) {
            total.push(index);
        }
        return total;
    }, [] as number[]);

    for (let i = 0; i < cutMaskLayers.length; i++) {
        const cutMaskLayer = layers[cutMaskLayers[i] - 1];
        const targetLayer = layers[cutMaskLayers[i]];
        const targetClientLayer = clientLayersMap[targetLayer.id || ''];
        const cutMaskClientLayer = clientLayersMap[cutMaskLayer.id || ''];

        if (!cutMaskClientLayer || !targetClientLayer) {
            continue;
        }
        const layerIndex = clientLayers.findIndex((item) => item.id === cutMaskClientLayer?.id?.toString());
        if (isTextLayer(cutMaskLayer) || isGroupLayer(cutMaskLayer) || cutMaskLayer.clipping) {
            clientLayers.splice(layerIndex, 1);
            continue;
        }
        const { widthRatio, heightRatio } = computerBgRatio({
            imgWidth: targetClientLayer.width,
            imgHeight: targetClientLayer.height,
            stageWidth: cutMaskClientLayer.width,
            stageHeight: cutMaskClientLayer.height,
        });

        const cutMaskData = cutMaskClientLayer.data as IImageLayerProps;
        const data = targetClientLayer.data as IImageLayerProps;
        const { imageTransform } = cutMaskData;
        if (cutMaskData.url) {
            data.maskURL = cutMaskData.url;
        } else {
            data.maskURL = await getUrlByCanvas(
                cutMaskLayer.name || '',
                cutMaskLayer.canvas as HTMLCanvasElement,
                uploadFile,
            );
        }
        data.maskType = MaskType.cut;
        cutMaskClientLayer.mode = ImgModeType.imgContainer;
        data.url = (targetClientLayer.data as IImageLayerProps).url;

        /**
         * @description 蒙版里的图片的偏移位置,
         * offsetX\offsetY\naturalHeight\naturalWidth\heightRatio\widthRatio 后面格式化JSON 数据的时候图片数据会用到
         */
        // leeft 差值 + (targetClientLayer.width - maskLayer.width) / 2) 打补丁方式，兼容 还编辑器 flex 水平垂直居中后的偏移量
        // offset  相对于 图片来算的
        imageTransform.offsetX =
            (targetClientLayer.left -
                cutMaskClientLayer.left +
                (targetClientLayer.width - cutMaskClientLayer.width) / 2) /
            targetClientLayer.width;
        imageTransform.offsetY =
            (targetClientLayer.top -
                cutMaskClientLayer.top +
                (targetClientLayer.height - cutMaskClientLayer.height) / 2) /
            targetClientLayer.height;

        // 换算scale
        if (widthRatio < heightRatio) {
            imageTransform.scale = toFixedNumber(targetClientLayer.width / cutMaskClientLayer.width);
        } else {
            imageTransform.scale = toFixedNumber(targetClientLayer.height / cutMaskClientLayer.height);
        }
        const { width, height } = targetClientLayer;
        imageTransform.widthRatio = widthRatio;
        imageTransform.heightRatio = heightRatio;
        data.imageTransform = imageTransform;
        data.naturalWidth = width;
        data.naturalHeight = height;
        data.minimumMask = true;
        targetClientLayer.width = cutMaskClientLayer.width;
        targetClientLayer.height = cutMaskClientLayer.height;
        targetClientLayer.left = cutMaskClientLayer.left;
        targetClientLayer.top = cutMaskClientLayer.top;
        targetClientLayer.mode = ImgModeType.imgContainer;

        if (
            cutMaskLayer.effects &&
            (cutMaskLayer.effects.dropShadow ||
                (cutMaskLayer.effects.outerGlow && cutMaskLayer.effects.outerGlow.enabled) ||
                cutMaskLayer.effects.stroke)
        ) {
            (targetClientLayer.data as IImageLayerProps).effect = {
                effectAlpha: 1,
                glow: parseImageGlow(cutMaskLayer),
                shadow: parseImageShadow(cutMaskLayer),
                stroke: parseImageStroke(cutMaskLayer),
                screenshotUrl: '',
            };
        }

        if (
            (targetClientLayer.height > stageHeight * 1.5 || targetClientLayer.width > stageWidth * 1.5) &&
            parseNoLimit
        ) {
            warnOrErrorCallback?.(
                `蒙版${cutMaskClientLayer.title}：不能大于当前画布1.5倍`, // svg蒙版解析出来会大几px所以修改了限制
                PsdParseWarnType.Error,
            );
        }

        clientLayers.splice(layerIndex, 1);
    }
};

/**
 * 是否为文本图层
 * @param layer
 */
export function isTextLayer(layer: ParseLayer) {
    return !!layer.text;
}

/**
 * 是否为矢量图层
 * @param layer
 */
export function isVectorLayer(layer: ParseLayer) {
    return !!layer.vectorMask?.paths.length && !layer.clipping;
}

/**
 * 是否为图片图层
 * @param layer
 */
export function isImageLayer(layer: ParseLayer) {
    if (layer.canvas && layer.blendMode !== 'normal') {
        return false;
    }
    return !!layer.canvas;
}

/**
 * 是否为背景图层名称
 * @param name
 */
export function isValidBackgroundLayerName(name: string) {
    return /^(bg|background|背景)$/i.test(name);
}

export const isBgLayer = (bgLayer: ParseLayer) => {
    if (!bgLayer) {
        return false;
    }

    if (bgLayer.nameSource === LayerNameSource.Background) {
        return true;
    }

    if (
        (bgLayer.children && bgLayer.children.length > 0) ||
        (!bgLayer.canvas && bgLayer.layerColor !== 'none') ||
        isTextLayer(bgLayer) ||
        !isValidBackgroundLayerName(bgLayer.name?.trim() || '')
    ) {
        return false;
    }

    return true;
};

export const isGroupLayer = (layer: ParseLayer) => {
    return layer.children && layer.children.length > 0;
};

interface Children<T> {
    children?: Tree<T>[];
}

interface Tree<T> extends Children<T> {}
export const traverseTreePostOrder = async <T>(parentNode: Tree<T>, node: Tree<T>, callback: Function) => {
    if (node.children && node.children.length > 0) {
        for (let i = 0; i < node.children.length; i++) {
            await traverseTreePostOrder(node, node.children[i] as Tree<T>, callback);
        }
    }

    await callback(parentNode, node);
};

// 判断所有像素是否含有透明度
export const canSaveToJpg = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');

    const data = (ctx?.getImageData(0, 0, canvas.width, canvas.height).data || []) as number[];

    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] !== 255) {
            return false;
        }
    }

    return true;
};

// 根据children的top left来判断是横版还是竖版
export const isVertical = (children: ParseLayer[]) => {
    const top = children[0].artboard?.rect?.top;
    const isVertical = children.every((item) => {
        return item.top === top;
    });

    if (isVertical) {
        return true;
    } else {
        return false;
    }
};

export function sliceCanvas(
    canvas: HTMLCanvasElement | undefined,
    sliceInfos: ParseLayer[],
    isVertical: boolean,
): HTMLCanvasElement[] {
    if (!canvas) throw new Error('Failed to get canvas.');
    const slices: HTMLCanvasElement[] = [];
    let offsetX = 0;
    let offsetY = 0;

    for (const sliceInfo of sliceInfos) {
        const sliceCanvas = document.createElement('canvas');
        const rect = sliceInfo.artboard?.rect || {
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
        };
        const width = rect.right - rect.left;
        const height = rect.bottom - rect.top;
        sliceCanvas.width = width;
        sliceCanvas.height = height;

        const ctx = sliceCanvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get 2D context.');

        const sx = isVertical ? 0 : offsetX;
        const sy = isVertical ? offsetY : 0;

        ctx.drawImage(canvas, sx, sy, width, height, 0, 0, width, height);

        slices.push(sliceCanvas);

        offsetX += isVertical ? 0 : width;
        offsetY += isVertical ? height : 0;
    }

    return slices;
}

export const isGradientColor = (layer: ParseLayer) => {
    const { vectorFill, effects } = layer;

    if (vectorFill && (vectorFill as EffectSolidGradient).colorStops) {
        return true;
    }

    if (
        effects?.disabled !== true &&
        effects &&
        effects.gradientOverlay &&
        effects.gradientOverlay[0]?.enabled &&
        (effects.gradientOverlay[0]?.gradient as EffectSolidGradient)?.colorStops
    ) {
        return true;
    }

    return false;
};

// 获取4个不同圆角值，如果相同则从原先的svgInfo里解析出radius
export const getFourCornerBorderRadius = (vectorOrigination: any) => {
    const keyDescriptorList = vectorOrigination?.keyDescriptorList;
    if (!keyDescriptorList || keyDescriptorList.length === 0 || !keyDescriptorList[0]?.keyOriginRRectRadii) return null;
    const borderRadius = keyDescriptorList[0].keyOriginRRectRadii;

    if (
        borderRadius.topLeft?.value === borderRadius.topRight?.value &&
        borderRadius.topLeft?.value === borderRadius.bottomLeft?.value &&
        borderRadius.topLeft?.value === borderRadius.bottomRight?.value
    ) {
        return null;
    }
    return {
        leftTop: borderRadius.topLeft?.value || 0,
        rightTop: borderRadius.topRight?.value || 0,
        leftBottom: borderRadius.bottomLeft?.value || 0,
        rightBottom: borderRadius.bottomRight?.value || 0,
    };
};

// 判断SVG图形是否为矩形、圆形
export const isRect = (layer: ParseLayer) => {
    const vectorOrigination = layer.vectorOrigination;
    if (getFourCornerBorderRadius(vectorOrigination)) {
        // 4个圆角值不同时，解析为polygon
        return false;
    }
    const keyDescriptorList = vectorOrigination?.keyDescriptorList;
    if (keyDescriptorList && keyDescriptorList.length === 1 && !keyDescriptorList[0]?.keyShapeInvalidated) {
        const keyOriginType = keyDescriptorList[0]?.keyOriginType;
        if (keyOriginType === 1 || keyOriginType === 2) {
            return true;
        }
        return false;
    }
    return false;
};

export const isEllipse = (layer: ParseLayer) => {
    const vectorOrigination = layer.vectorOrigination;
    const keyDescriptorList = vectorOrigination?.keyDescriptorList;
    if (keyDescriptorList && keyDescriptorList.length === 1 && !keyDescriptorList[0]?.keyShapeInvalidated) {
        const keyOriginType = keyDescriptorList[0]?.keyOriginType;
        if (keyOriginType === 5) {
            return true;
        }
        return false;
    }
    return false;
};

// 获取中间那个点的颜色
// export const getCenterColor = (
//     prevColorStop: ColorStop,
//     centerColor: ColorStop,
//     nextColorStop: ColorStop,
// ) => {
//     const lerp = (v0, v1, t) => {
//         return v0 + t * (v1 - v0);
//     };

//     const t =
//         (centerColor.location - prevColorStop.location) /
//         (nextColorStop.location - prevColorStop.location);
//     const r = lerp((prevColorStop.color as RGBA).r, (nextColorStop.color as RGBA).r, t);
//     const g = lerp((prevColorStop.color as RGBA).g, (nextColorStop.color as RGBA).g, t);
//     const b = lerp((prevColorStop.color as RGBA).b, (nextColorStop.color as RGBA).b, t);

//     return tinycolor({ r, g, b, a: (centerColor.color as RGBA).a }).toString();
// };

export const getStrokeGradientColor = (layer: ParseLayer) => {
    if (
        !(
            layer &&
            layer.vectorStroke &&
            layer.vectorStroke.content &&
            (layer.vectorStroke.content as EffectSolidGradient).colorStops
        )
    ) {
        return null;
    }

    const colorStops = (layer.vectorStroke.content as EffectSolidGradient).colorStops;
    const opacityStops = (layer.vectorStroke.content as EffectSolidGradient).opacityStops;
    const fillGradient = { angle: 0, colors: [] as any, type: 'linear' };
    fillGradient.angle = (layer.vectorStroke.content as any)?.angle || 0;
    fillGradient.type = (layer.vectorStroke.content as any)?.type || 'linear';

    if (colorStops.length === 1 && opacityStops.length > 0) {
        const colorStop = colorStops[0];

        for (let i = 0; i < opacityStops.length; i++) {
            const opacityStop = opacityStops[i];
            fillGradient.colors.push({
                color: tinycolor(colorStop.color as RGBA)
                    .setAlpha(opacityStop.opacity)
                    .toHex8String(),
                value: opacityStop.location,
            });
        }

        for (let i = 0; i < opacityStops.length; i++) {
            if (opacityStops[i].location > colorStop.location) {
                fillGradient.colors.splice(i, 0, {
                    color: tinycolor(colorStop.color as RGBA).toHex8String(),
                    value: colorStop.location,
                });
                break;
            }
        }
    } else {
        for (let i = 0; i < colorStops.length; i++) {
            const colorStop = colorStops[i];

            // FIXME  tinycolor(colorStop.color as RGBA)如果不是rgba可能会错  但是现在不知道有多少种情况
            fillGradient.colors.push({
                color: tinycolor(colorStop.color as RGBA).toHex8String(),
                value: colorStop.location,
            });
        }
    }
    return fillGradient;
};
export const getGradientColor = (layer: ParseLayer) => {
    const { vectorFill, effects } = layer;
    const fillGradient = { angle: 0, colors: [] as any, type: 'linear' };
    if (
        effects &&
        effects.gradientOverlay &&
        effects.gradientOverlay[0]?.enabled &&
        (effects.gradientOverlay[0]?.gradient as EffectSolidGradient)?.colorStops
    ) {
        const colorStops = (effects.gradientOverlay[0]?.gradient as EffectSolidGradient)?.colorStops;
        const opacityStops = (effects.gradientOverlay[0]?.gradient as EffectSolidGradient)?.opacityStops;
        const reverse = (effects.gradientOverlay[0] as any)?.reverse || false;
        fillGradient.angle = (effects.gradientOverlay[0] as any)?.angle || 0;
        if (reverse) {
            fillGradient.angle = fillGradient.angle + 180;
        }
        fillGradient.type = (effects.gradientOverlay[0] as any)?.type || 'linear';

        if (colorStops.length === 1 && opacityStops.length > 0) {
            const colorStop = colorStops[0];

            for (let i = 0; i < opacityStops.length; i++) {
                const opacityStop = opacityStops[i];
                fillGradient.colors.push({
                    color: tinycolor(colorStop.color as RGBA)
                        .setAlpha(opacityStop.opacity)
                        .toHex8String(),
                    value: opacityStop.location,
                });
            }

            for (let i = 0; i < opacityStops.length; i++) {
                if (opacityStops[i].location < colorStop.location) {
                    fillGradient.colors.splice(i, 0, {
                        color: tinycolor(colorStop.color as RGBA).toHex8String(),
                        value: colorStop.location,
                    });
                    break;
                }
            }
        } else {
            for (let i = 0; i < colorStops.length; i++) {
                const colorStop = colorStops[i];

                // FIXME  tinycolor(colorStop.color as RGBA)如果不是rgba可能会错  但是现在不知道有多少种情况
                // if (i !== 0 && i !== colorStops.length - 1) {
                //     fillGradient.colors.push({
                //         color: getCenterColor(colorStops[i - 1], colorStops[i], colorStops[i + 1]),
                //         value: colorStop.location,
                //     });
                // } else {
                //     fillGradient.colors.push({
                //         color: tinycolor(colorStop.color as RGBA).toString(),
                //         value: colorStop.location,
                //     });
                // }

                fillGradient.colors.push({
                    color: tinycolor(colorStop.color as RGBA).toHex8String(),
                    value: colorStop.location,
                });
            }
        }

        return fillGradient;
    }

    if (vectorFill && (vectorFill as EffectSolidGradient).colorStops) {
        const colorStops = (vectorFill as EffectSolidGradient).colorStops;
        const opacityStops = (vectorFill as EffectSolidGradient).opacityStops;
        fillGradient.angle = (vectorFill as any)?.angle || 0;
        fillGradient.type = (vectorFill as any)?.style || 'linear';
        if (colorStops.length === 1 && opacityStops.length > 0) {
            const colorStop = colorStops[0];

            for (let i = 0; i < opacityStops.length; i++) {
                const opacityStop = opacityStops[i];
                fillGradient.colors.push({
                    color: tinycolor(colorStop.color as RGBA)
                        .setAlpha(opacityStop.opacity)
                        .toHex8String(),
                    value: opacityStop.location,
                });
            }

            for (let i = 0; i < opacityStops.length; i++) {
                if (opacityStops[i].location > colorStop.location) {
                    fillGradient.colors.splice(i, 0, {
                        color: tinycolor(colorStop.color as RGBA).toHex8String(),
                        value: colorStop.location,
                    });
                    break;
                }
            }
        } else {
            for (let i = 0; i < colorStops.length; i++) {
                const colorStop = colorStops[i];

                // FIXME  tinycolor(colorStop.color as RGBA)如果不是rgba可能会错  但是现在不知道有多少种情况
                fillGradient.colors.push({
                    color: tinycolor(colorStop.color as RGBA).toHex8String(),
                    value: colorStop.location,
                });
            }
        }

        return fillGradient;
    }

    return fillGradient;
};
